import React from 'react';
import { Link } from 'react-router-dom';
import { Pin, MessageSquare } from 'lucide-react';

/**
 * PinnedPostsStrip — compact horizontal-row rendering of currently
 * pinned posts. Replaces the old "render every pinned post as a full
 * PostCard at the top of the feed" pattern, which was eating too much
 * vertical real estate now that we allow up to 5 simultaneous pins.
 *
 * Each row is title + small thumbnail + byline + comment count. Clicking
 * the row opens the post detail page.
 *
 * Self-hides when there are no pinned posts so we don't render an empty
 * "Pinned" header.
 */
export function PinnedPostsStrip({ pinnedPosts = [] }) {
  if (!pinnedPosts || pinnedPosts.length === 0) return null;

  return (
    <div
      className="mb-6 rounded-xl border border-amber-200/70 bg-amber-50/50 dark:bg-amber-950/20 dark:border-amber-900/40"
      data-testid="pinned-posts-strip"
    >
      <div className="flex items-center gap-2 px-4 pt-3 pb-1">
        <Pin className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400" />
        <span className="text-xs font-semibold uppercase tracking-wider text-amber-700 dark:text-amber-300">
          Pinned
        </span>
        <span className="text-xs text-muted-foreground">
          · {pinnedPosts.length} post{pinnedPosts.length === 1 ? '' : 's'}
        </span>
      </div>
      <ul className="divide-y divide-amber-200/50 dark:divide-amber-900/30">
        {pinnedPosts.map((post) => (
          <li key={post.id} data-testid={`pinned-strip-row-${post.id}`}>
            <Link
              to={`/post/${post.id}`}
              className="flex items-center gap-3 px-4 py-2.5 hover:bg-amber-100/40 dark:hover:bg-amber-900/20 transition-colors"
            >
              {/* Thumbnail or letter avatar */}
              {post.image_url || (post.images && post.images[0]) ? (
                <img
                  src={post.image_url || post.images[0]}
                  alt=""
                  className="w-10 h-10 rounded-md object-cover flex-shrink-0"
                  loading="lazy"
                />
              ) : (
                <div className="w-10 h-10 rounded-md bg-amber-200/60 dark:bg-amber-900/40 flex items-center justify-center flex-shrink-0">
                  <Pin className="w-4 h-4 text-amber-700/70 dark:text-amber-300/70" />
                </div>
              )}

              <div className="min-w-0 flex-1">
                <p className="font-medium text-sm leading-tight truncate">
                  {post.title || '(untitled)'}
                </p>
                <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
                  <span className="truncate">
                    {post.author_username ? `by ${post.author_username}` : ''}
                  </span>
                  {typeof post.comments_count === 'number' && (
                    <span className="flex items-center gap-1 flex-shrink-0">
                      <MessageSquare className="w-3 h-3" />
                      {post.comments_count}
                    </span>
                  )}
                </div>
              </div>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default PinnedPostsStrip;
